import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import { useModalContext } from '../../../contexts/ModalContext'
import TextField from '@mui/material/TextField'
import { Container, FormContainer } from './styles'
import * as NotificationService from '../../../services/notifications'
import Button from '@mui/material/Button'
import { Typography, Chip, Divider } from '@mui/material'

const CampaignTypeLabel = {
    ONE_TIME: 'Campanha Única',
    CAMPAIGN_WINDOW: 'Janela de Campanha'
}

const StatusLabel = {
    APPROVED: { text: 'APROVADO', color: '#2e7d32' },
    PENDING: { text: 'PENDENTE', color: '#1565c0' },
    DENIED: { text: 'NEGADO', color: '#c62828' }
}

function formatDate(dateStr) {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

function getMidpointDate(startDate, endDate) {
    if (!startDate || !endDate) return null
    const start = new Date(startDate).getTime()
    const end = new Date(endDate).getTime()
    return new Date(start + (end - start) / 2)
}

const ApproveOrDenyModal = ({ updateTable, notification }) => {
    const { setModal } = useModalContext('')
    const [reason, setReason] = useState(notification.reason ?? '')

    const handleClose = () => setModal(null)

    const status = StatusLabel[notification.status] || { text: notification.status, color: 'gray' }
    const isOneTime = notification.campaignType === 'ONE_TIME'
    const isCampaignWindow = notification.campaignType === 'CAMPAIGN_WINDOW'
    const isPending = notification.status === 'PENDING'

    const approve = async (event) => {
        event.preventDefault()

        await NotificationService.approve({ id: notification.id })
            .then(() => {
                alert('Notificação aprovada!')
                setModal(null)
                updateTable()
            })
            .catch(() => alert('Erro ao aprovar esta notificação!'))
    }

    const deny = async (event) => {
        event.preventDefault()

        if (!reason.trim()) {
            alert('Informe o motivo da recusa.')
            return
        }

        await NotificationService.deny({ id: notification.id, reason })
            .then(() => {
                alert('Notificação negada!')
                setModal(null)
                updateTable()
            })
            .catch(() => alert('Erro ao negar esta notificação!'))
    }

    const midpoint = isCampaignWindow ? getMidpointDate(notification.startDate, notification.endDate) : null

    return (
        <Modal
            open={true}
            onClose={handleClose}
            aria-labelledby='modal-modal-title'
            aria-describedby='modal-modal-description'
        >
            <Container>
                <Box
                    m={3}
                    mb={1}
                    fontWeight='bold'
                    color='#333'
                    fontSize={{
                        xs: 'h7.fontSize',
                        sm: 'h7.fontSize',
                        md: 'h6.fontSize',
                        xl: 'h5.fontSize'
                    }}
                >
                    Gerenciar Notificação
                </Box>

                <FormContainer>
                    {/* Header info */}
                    <Box display='flex' alignItems='center' gap={2} mb={2} flexWrap='wrap'>
                        <Chip
                            label={status.text}
                            size='small'
                            sx={{ backgroundColor: status.color, color: 'white', fontWeight: 'bold' }}
                        />
                        <Chip
                            label={CampaignTypeLabel[notification.campaignType] || notification.campaignType}
                            size='small'
                            color={isOneTime ? 'primary' : 'secondary'}
                            variant='outlined'
                        />
                        <Typography variant='body2' color='textSecondary'>
                            ID: {notification.id} | Mecânica: {notification.mechanicId}
                        </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Campaign content */}
                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary'>
                            Título
                        </Typography>
                        <Typography variant='body1' fontWeight='bold'>
                            {notification.title}
                        </Typography>
                    </Box>

                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary'>
                            Conteúdo
                        </Typography>
                        <Typography variant='body1'>{notification.text}</Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Dates section */}
                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary' mb={1}>
                            Programação
                        </Typography>

                        {isOneTime && (
                            <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1.5 }}>
                                {notification.scheduledDate ? (
                                    <Typography variant='body2'>
                                        <b>Agendado para:</b> {formatDate(notification.scheduledDate)}
                                    </Typography>
                                ) : (
                                    <Typography variant='body2' color='textSecondary'>
                                        Envio imediato após aprovação (próxima execução do scheduler às 10:00 BRT)
                                    </Typography>
                                )}
                            </Box>
                        )}

                        {isCampaignWindow && (
                            <Box sx={{ background: '#f5f5f5', borderRadius: 1, p: 1.5 }}>
                                <Typography variant='body2'>
                                    <b>Início:</b> {formatDate(notification.startDate)}
                                </Typography>
                                <Typography variant='body2'>
                                    <b>Fim:</b> {formatDate(notification.endDate)}
                                </Typography>
                                {midpoint && (
                                    <Typography variant='body2' color='textSecondary' mt={0.5}>
                                        Lembrete previsto em: {formatDate(midpoint.toISOString())}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>

                    {/* Sent flags */}
                    <Box mb={2}>
                        <Typography variant='subtitle2' color='textSecondary' mb={1}>
                            Status de Envio
                        </Typography>
                        <Box display='flex' gap={1} flexWrap='wrap'>
                            {isOneTime && (
                                <Chip
                                    label={notification.oneTimeSent ? 'Enviado' : 'Não enviado'}
                                    size='small'
                                    color={notification.oneTimeSent ? 'success' : 'default'}
                                    variant={notification.oneTimeSent ? 'filled' : 'outlined'}
                                />
                            )}
                            {isCampaignWindow && (
                                <>
                                    <Chip
                                        label={
                                            notification.startNotificationSent ? 'Início enviado' : 'Início pendente'
                                        }
                                        size='small'
                                        color={notification.startNotificationSent ? 'success' : 'default'}
                                        variant={notification.startNotificationSent ? 'filled' : 'outlined'}
                                    />
                                    <Chip
                                        label={
                                            notification.reminderNotificationSent
                                                ? 'Lembrete enviado'
                                                : 'Lembrete pendente'
                                        }
                                        size='small'
                                        color={notification.reminderNotificationSent ? 'success' : 'default'}
                                        variant={notification.reminderNotificationSent ? 'filled' : 'outlined'}
                                    />
                                </>
                            )}
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    {/* Reason field */}
                    {notification.status === 'DENIED' && notification.reason && (
                        <Box
                            mb={2}
                            sx={{ background: '#fff3f3', borderRadius: 1, p: 1.5, border: '1px solid #ffcdd2' }}
                        >
                            <Typography variant='subtitle2' color='error'>
                                Motivo da recusa
                            </Typography>
                            <Typography variant='body2'>{notification.reason}</Typography>
                        </Box>
                    )}

                    {isPending && (
                        <TextField
                            name='reason'
                            label='Motivo (obrigatório para negar)'
                            value={reason}
                            variant='outlined'
                            style={{ width: '100%' }}
                            onChange={(event) => setReason(event.target.value)}
                            multiline
                            rows={4}
                        />
                    )}
                </FormContainer>

                <Box display='flex' justifyContent='flex-end' padding='16px 20px' gap='8px'>
                    {isPending && (
                        <>
                            <Button
                                variant='contained'
                                sx={{
                                    background: '#2e7d32',
                                    '&:hover': { background: '#1b5e20' }
                                }}
                                onClick={(event) => approve(event)}
                            >
                                Aprovar
                            </Button>
                            <Button
                                variant='contained'
                                sx={{
                                    background: '#c62828',
                                    '&:hover': { background: '#b71c1c' }
                                }}
                                onClick={(event) => deny(event)}
                            >
                                Negar
                            </Button>
                        </>
                    )}
                    <Button variant='outlined' onClick={handleClose}>
                        Fechar
                    </Button>
                </Box>
            </Container>
        </Modal>
    )
}

export default ApproveOrDenyModal
